/**
 * Abacus AMP — EU Commercialization Shortlist (v1)
 * ------------------------------------------------
 * Buyer-specific screening dashboard for Cameron Rodd / Abacus Medicine Pharmaceuticals.
 * Scores assets against Cameron's stated criteria, lets you edit fit flags inline,
 * and exports the final shortlist as the Excel file Cameron asked for.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Download, ExternalLink, RefreshCcw, Sparkles, Loader2, Info, ChevronDown, ChevronUp, Globe } from 'lucide-react';

const API = '/api';

const FIT_STATUS_OPTIONS = [
  { value: 'not_screened', label: 'Auto (use score)' },
  { value: 'fit', label: 'Mark Fit (override)' },
  { value: 'borderline', label: 'Mark Borderline (override)' },
  { value: 'out_of_scope', label: 'Out of scope (exclude)' },
];

// Tier label derived from the auto score — shown as the dropdown's display value when
// fit_status is "not_screened" so the column always reflects the asset's current state
// rather than the confusing "Not screened" default.
const autoTierLabel = (score) => {
  if (score >= 65) return 'Strong Fit (auto)';
  if (score >= 50) return 'Fit (auto)';
  if (score >= 25) return 'Borderline (auto)';
  return 'Below threshold (auto)';
};

const EU_RIGHTS_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'available', label: 'Available' },
  { value: 'negotiable', label: 'Negotiable' },
  { value: 'taken', label: 'Taken' },
];

const REG_READINESS_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'eu_filed', label: 'EU-filed' },
  { value: 'fda_approved_eu_bridge', label: 'FDA-approved (EU bridge)' },
  { value: 'ph3_pivotal_starting', label: 'Phase 3 pivotal starting' },
  { value: 'not_ready', label: 'Not ready' },
];

const fitBadgeClass = (score) => {
  if (score >= 50) return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
  if (score >= 25) return 'bg-amber-100 text-amber-800 border border-amber-200';
  return 'bg-slate-100 text-slate-600 border border-slate-200';
};

// Multi-state Abacus engagement tracking (replaces the old binary `abacus_known` flag).
// `not_seen` is the default for unflagged biotechs and earns the +5 net-new score bonus.
const ABACUS_STATUS_OPTIONS = [
  { value: 'not_seen', label: 'Not seen by Abacus' },
  { value: 'under_evaluation', label: 'Under evaluation' },
  { value: 'evaluated_passed', label: 'Evaluated · passed' },
  { value: 'in_portfolio', label: 'In Abacus portfolio' },
];
const ABACUS_STATUS_LABELS = ABACUS_STATUS_OPTIONS.reduce((m, o) => ({ ...m, [o.value]: o.label }), {});
const abacusStatusPillClass = (status) => ({
  not_seen: 'bg-sky-100 text-sky-800 border-sky-200',
  under_evaluation: 'bg-amber-100 text-amber-800 border-amber-200',
  evaluated_passed: 'bg-rose-100 text-rose-800 border-rose-200',
  in_portfolio: 'bg-emerald-100 text-emerald-800 border-emerald-200',
}[status] || 'bg-slate-100 text-slate-700 border-slate-200');

// Human-friendly "2h ago" formatter — used for `last_web_enrichment_at` per asset row.
const relTime = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  return `${mo}mo ago`;
};

export default function AbacusShortlistPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showScoring, setShowScoring] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [taFilter, setTaFilter] = useState('all');
  const [minScore, setMinScore] = useState(25);
  const [hideKnown, setHideKnown] = useState(false);
  const [orphanOnly, setOrphanOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  // Web enrichment state — modal-driven, one asset at a time for v1
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichApplying, setEnrichApplying] = useState(false);
  const [enrichTargetId, setEnrichTargetId] = useState(null);
  const [enrichProposal, setEnrichProposal] = useState(null); // { asset_name, company_name, current, proposed, citations }
  const [enrichAccepted, setEnrichAccepted] = useState({}); // { field: true }
  // Bulk enrichment state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkJob, setBulkJob] = useState(null); // { job_id, total, completed, ok_count, ... }
  const [bulkStarting, setBulkStarting] = useState(false);
  const [bulkScope, setBulkScope] = useState('borderline');
  const [bulkAutoApply, setBulkAutoApply] = useState('conservative');
  // Last completed bulk-enrich job (for the persistent banner)
  const [lastRun, setLastRun] = useState(null);
  // Review queues — opened from the badges on the last-run banner
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewActioningId, setReviewActioningId] = useState(null);
  const [appliedOpen, setAppliedOpen] = useState(false);
  const [appliedLoading, setAppliedLoading] = useState(false);
  const [appliedItems, setAppliedItems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (taFilter !== 'all') params.ta = taFilter;
      if (minScore > 0) params.min_score = minScore;
      if (hideKnown) params.include_known = false;
      const res = await axios.get(`${API}/abacus/shortlist`, { params, withCredentials: true });
      setData(res.data);
      setAuthError(false);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthError(true);
      } else {
        toast.error('Failed to load Abacus shortlist', { description: e?.response?.data?.detail || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taFilter, minScore, hideKnown]);

  // Fetch last completed bulk-enrich job once on mount + after any new job completes
  const refreshLastRun = async () => {
    try {
      const res = await axios.get(`${API}/abacus/bulk-enrich/last-run`, { withCredentials: true });
      setLastRun(res.data?.last_run || null);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => { refreshLastRun(); }, []);

  // Open the review queue (clicking the "pending review" badge)
  const openReview = async () => {
    setReviewOpen(true);
    setReviewLoading(true);
    try {
      const res = await axios.get(`${API}/abacus/web-enrich/pending`, { withCredentials: true });
      setReviewItems(res.data?.items || []);
    } catch (e) {
      toast.error('Could not load review queue', { description: e?.response?.data?.detail || e.message });
    } finally {
      setReviewLoading(false);
    }
  };

  const actionReviewItem = async (item, action) => {
    setReviewActioningId(`${item.asset_id}__${item.field}`);
    try {
      const url = `${API}/abacus/web-enrich/pending/${action}`;
      await axios.post(url, { asset_id: item.asset_id, field: item.field, pending_id: item.pending_id }, { withCredentials: true });
      // Drop the actioned row from local state
      setReviewItems(prev => prev.filter(r => !(r.asset_id === item.asset_id && r.field === item.field)));
      toast.success(action === 'accept' ? `Applied ${item.field} to ${item.asset_name}` : `Rejected ${item.field} on ${item.asset_name}`);
      refreshLastRun();
    } catch (e) {
      toast.error(`Could not ${action} finding`, { description: e?.response?.data?.detail || e.message });
    } finally {
      setReviewActioningId(null);
    }
  };

  // Open the auto-applied audit panel (clicking the "fields auto-applied" badge)
  const openApplied = async () => {
    setAppliedOpen(true);
    setAppliedLoading(true);
    try {
      const res = await axios.get(`${API}/abacus/web-enrich/auto-applied`, { withCredentials: true });
      setAppliedItems(res.data?.items || []);
    } catch (e) {
      toast.error('Could not load auto-applied changes', { description: e?.response?.data?.detail || e.message });
    } finally {
      setAppliedLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    const q = search.trim().toLowerCase();
    let items = data.items;
    if (orphanOnly) items = items.filter(r => r.orphan);
    if (!q) return items;
    return items.filter(r =>
      (r.asset_name || '').toLowerCase().includes(q) ||
      (r.company_name || '').toLowerCase().includes(q) ||
      (r.indication || '').toLowerCase().includes(q) ||
      (r.therapeutic_area || '').toLowerCase().includes(q)
    );
  }, [data, search, orphanOnly]);

  const patchAsset = async (assetId, patch) => {
    setSavingId(assetId);
    try {
      await axios.patch(`${API}/abacus/assets/${assetId}`, patch, { withCredentials: true });
      // Optimistic local update
      setData(prev => ({
        ...prev,
        items: prev.items.map(r => r.asset_id === assetId ? { ...r, ...patch } : r),
      }));
      toast.success('Saved');
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthError(true);
      } else {
        toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
      }
    } finally {
      setSavingId(null);
    }
  };

  const toggleKnown = async (companyId, current) => {
    try {
      await axios.patch(`${API}/abacus/biotechs/${companyId}/known`,
        { abacus_known: !current }, { withCredentials: true });
      setData(prev => ({
        ...prev,
        items: prev.items.map(r =>
          r.company_id === companyId ? { ...r, abacus_known: !current } : r
        ),
      }));
      toast.success(!current ? 'Marked as Abacus-known' : 'Removed Abacus-known flag');
    } catch (e) {
      toast.error('Update failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  const openEnrich = async (assetId) => {
    setEnrichTargetId(assetId);
    setEnrichOpen(true);
    setEnrichProposal(null);
    setEnrichAccepted({});
    setEnrichLoading(true);
    try {
      const res = await axios.post(`${API}/abacus/assets/${assetId}/enrich-from-web`,
        {}, { withCredentials: true });
      setEnrichProposal(res.data);
      // Default-accept fields that have a meaningful proposed_value AND differ from current
      const accept = {};
      for (const [field, block] of Object.entries(res.data.proposed || {})) {
        if (!block || typeof block !== 'object') continue;
        const pv = block.proposed_value;
        const cv = res.data.current?.[field];
        if (pv && pv !== 'unknown' && pv !== 'null' && String(pv) !== String(cv || '')) {
          accept[field] = true;
        }
      }
      setEnrichAccepted(accept);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error('Web enrichment failed', { description: msg });
      setEnrichOpen(false);
    } finally {
      setEnrichLoading(false);
    }
  };

  const startBulkEnrich = async () => {
    setBulkStarting(true);
    try {
      const res = await axios.post(`${API}/abacus/bulk-enrich/start`,
        { scope: bulkScope, auto_apply: bulkAutoApply, min_score: minScore },
        { withCredentials: true });
      setBulkJob({ ...res.data, completed: 0, ok_count: 0, error_count: 0, auto_applied_total: 0, review_total: 0, status: 'running' });
      toast.success(`Bulk enrichment started — ${res.data.total} assets queued`);
    } catch (e) {
      toast.error('Failed to start bulk enrichment', { description: e?.response?.data?.detail || e.message });
      setBulkOpen(false);
    } finally {
      setBulkStarting(false);
    }
  };

  // Poll job status every 3 seconds while running. The shortlist table is NOT
  // auto-refreshed when the job completes — the user must explicitly click
  // "Refresh shortlist" from the persistent banner so they always know what
  // they're looking at.
  useEffect(() => {
    if (!bulkJob?.job_id || bulkJob?.status === 'completed') return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/abacus/bulk-enrich/${bulkJob.job_id}`, { withCredentials: true });
        setBulkJob(res.data);
        if (res.data.status === 'completed') {
          clearInterval(interval);
          toast.success(
            `Bulk enrichment finished — ${res.data.auto_applied_total} fields auto-applied · ${res.data.review_total} pending review.`,
            { description: 'Click "Refresh shortlist" in the banner above the table when ready.' }
          );
          refreshLastRun();
        }
      } catch (_e) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [bulkJob?.job_id, bulkJob?.status]); // eslint-disable-line

  const applyEnrich = async () => {
    if (!enrichProposal || !enrichTargetId) return;
    const acceptedFields = Object.keys(enrichAccepted).filter(f => enrichAccepted[f]);
    if (acceptedFields.length === 0) {
      toast.info('No fields selected to apply');
      return;
    }
    setEnrichApplying(true);
    try {
      const res = await axios.post(
        `${API}/abacus/assets/${enrichTargetId}/enrich-from-web/apply`,
        {
          accepted_fields: acceptedFields,
          proposal: { ...enrichProposal.proposed, _citations: enrichProposal.citations },
        },
        { withCredentials: true },
      );
      toast.success(`Applied ${res.data.applied.length} fields`,
        { description: res.data.skipped?.length ? `Skipped: ${res.data.skipped.length}` : undefined });
      setEnrichOpen(false);
      load(); // refresh shortlist + KPIs (asset may have promoted to a higher tier)
    } catch (e) {
      toast.error('Apply failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setEnrichApplying(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Always export all 3 tiers (Strong Fit + Fit + Borderline) in one file.
      // Pass orphan_only when the Orphan KPI filter is active so the export
      // matches what's on screen.
      const params = {};
      if (taFilter !== 'all') params.ta = taFilter;
      if (orphanOnly) params.orphan_only = true;
      const res = await axios.get(`${API}/abacus/shortlist.xlsx`, {
        params, responseType: 'blob', withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const suffix = orphanOnly ? '_orphan' : '';
      a.download = `foresightrx_abacus_shortlist${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthError(true);
      } else {
        toast.error('Export failed', { description: e?.response?.data?.detail || e.message });
      }
    } finally {
      setExporting(false);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Auto-mark all early-stage / preclinical / unknown-stage assets as "out of scope"? This is idempotent and skips anything you have already flagged as fit/borderline.')) return;
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/abacus/seed`, {}, { withCredentials: true });
      toast.success(`Seeded — ${res.data.marked_out_of_scope} assets marked out of scope`);
      load();
    } catch (e) {
      toast.error('Seed failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSeeding(false);
    }
  };

  const kpis = data?.kpis;
  const taOptions = data?.ta_options || [];

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="abacus-shortlist-page">
      {/* Auth-error fallback — appears when /api/abacus/* returns 401 (typically because backend deploy is stale) */}
      {authError && (
        <div className="mb-5 border border-amber-300 bg-amber-50 rounded-lg p-5 flex items-start gap-4" data-testid="abacus-auth-error">
          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-lg">!</div>
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Can't load the Abacus shortlist right now.</div>
            <div className="text-sm text-amber-800 mt-1">
              The most likely cause: your production backend hasn't picked up the new Abacus code yet (the deploy may have only refreshed the website but not the API service).
            </div>
            <div className="text-xs text-amber-700 mt-2 space-y-1">
              <div><b>Try first:</b> Sign out and sign back in, then reload this page.</div>
              <div><b>If still broken:</b> Trigger a fresh deploy from your Emergent panel. If that doesn't help, contact Emergent Support.</div>
              <div><b>Workaround for today:</b> This page works on your preview URL — you can export Cameron's Excel from there.</div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => { setAuthError(false); load(); }} data-testid="abacus-retry-btn">
                <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Retry
              </Button>
              <Button size="sm" variant="outline" onClick={() => { window.location.href = '/login'; }} data-testid="abacus-relogin-btn">
                Sign in again
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">
            <Sparkles className="w-3.5 h-3.5" />
            Buyer-specific workflow
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Abacus Medicine — EU Commercialization Shortlist
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Screens our universe against the AMP conference deck (May 2026) + Cameron Rodd's stated criteria.
            EU rights available/negotiable · ready-to-file in EU OR FDA-approved (EU bridge) OR Ph3-pivotal starting ·
            specialty/rare-disease only · hospital-specialist channel · no Phase IV clinical investment.
            Cardio uplift = Cardioplexol franchise fit (acquired Swiss Cardio Tech 2025). Orphan / rare-disease bonus per deck Orphan carve-outs.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
            disabled={bulkJob?.status === 'running'}
            data-testid="abacus-bulk-enrich-btn"
            className="border-[#00709B] text-[#00709B] hover:bg-[#00709B]/5"
          >
            <Globe className="w-4 h-4" />
            <span className="ml-2">
              {bulkJob?.status === 'running'
                ? `Enriching ${bulkJob.completed}/${bulkJob.total}…`
                : 'Bulk Web Enrich'}
            </span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSeed}
            disabled={seeding}
            data-testid="abacus-seed-btn"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            <span className="ml-2">Auto-exclude early-stage</span>
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-[#00709B] hover:bg-[#005a7d] text-white"
            data-testid="abacus-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="ml-2">Export shortlist (.xlsx)</span>
          </Button>
        </div>
      </div>

      {/* How scoring works — collapsible help panel */}
      <div className="mb-5 border border-slate-200 rounded-lg bg-white" data-testid="abacus-scoring-help">
        <button
          onClick={() => setShowScoring(s => !s)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50 rounded-lg transition"
          data-testid="abacus-scoring-help-toggle"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Info className="w-4 h-4 text-[#00709B]" />
            How scoring works — fitness tier definitions
          </div>
          {showScoring ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showScoring && (
          <div className="px-4 pb-4 pt-1 border-t border-slate-100 grid md:grid-cols-2 gap-6 text-xs text-slate-700">
            {/* Tier thresholds */}
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Fitness tiers</div>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center min-w-[60px] px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">Strong Fit</span>
                  <div>
                    <div className="font-semibold">Score ≥ 65</div>
                    <div className="text-slate-500">Ready to pitch to Cameron. Has clear regulatory readiness + EU rights + named-TA fit.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center min-w-[60px] px-2 py-0.5 rounded text-[11px] font-bold bg-teal-100 text-teal-800 border border-teal-200">Fit</span>
                  <div>
                    <div className="font-semibold">Score 50–64</div>
                    <div className="text-slate-500">Strong candidate, but missing 1 input (e.g., EU rights status unclear, or TA-only signal without reg readiness yet).</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center min-w-[60px] px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">Borderline</span>
                  <div>
                    <div className="font-semibold">Score 25–49</div>
                    <div className="text-slate-500">Worth screening — has TA match or Cardio uplift, but reg readiness + EU rights still need to be verified.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center min-w-[60px] px-2 py-0.5 rounded text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-200">Out of Scope</span>
                  <div>
                    <div className="font-semibold">Hard-excluded</div>
                    <div className="text-slate-500">Phase 1/2, preclinical, EU rights taken, or manually marked out of scope. Cameron explicitly excludes these (no Phase IV clinical investment).</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Score components */}
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">How a score is built</div>
              <div className="space-y-1.5">
                <ScoreRow plus="+30" label="EU-filed" desc="Submission active with EMA — top of Cameron's preference" />
                <ScoreRow plus="+25" label="FDA-approved (EU bridge feasible)" desc="Deck-cited pathway: FDA 505(b)(2) → EMA Hybrid Article 10(3)" />
                <ScoreRow plus="+15" label="Phase 3 pivotal starting" desc="Option-deal candidate — Abacus pays option/milestones, no clinical funding" />
                <ScoreRow plus="+20" label="EU rights available" desc="Pan-EU or EU4+1+Skandi minimum, Germany must be in scope" />
                <ScoreRow plus="+10" label="EU rights negotiable" desc="Asset owner open to discussion" />
                <ScoreRow plus="+10" label="TA fit (named bucket)" desc="Immunology · Heme · CV · Neurology + 22 named sub-buckets" />
                <ScoreRow plus="+10" label="Cardio uplift" desc="Cardioplexol franchise fit — acquisition-favored (Swiss Cardio Tech acquired 2025)" />
                <ScoreRow plus="+10" label="Orphan / rare-disease" desc="Deck has explicit Orphan carve-out sub-buckets in I&I, Heme & Neuro" />
                <ScoreRow plus="+5" label="Net-new to Abacus" desc="Biotech NOT already in Cameron's own list — surfaces deltas first" />
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                <b>Hard excludes</b> (score → 0): Phase 1/2/preclinical, EU rights = taken, manual "Out of scope" flag.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* KPIs — click to filter */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3" data-testid="abacus-kpis">
          <Kpi label="In-Scope Universe" value={kpis.total_in_universe} sub={kpis.companies?.in_scope ? `${kpis.companies.in_scope} companies covered · Ph3 / commercial / approved` : null} tone="slate"
               active={minScore === 0 && !orphanOnly} onClick={() => { setMinScore(0); setOrphanOnly(false); }} testId="kpi-universe"
               title="Assets that scored ≥ 25 — your actual shortlist (Strong Fit + Fit + Borderline). The 'companies covered' number is the DISTINCT count of biotechs whose assets populate this shortlist. Click to clear all tier/orphan filters." />
          <Kpi label="Strong Fit (≥65)" value={kpis.strong_fit ?? 0} sub={kpis.companies?.strong_fit ? `${kpis.companies.strong_fit} companies` : null} tone="emerald"
               active={minScore === 65 && !orphanOnly} onClick={() => { setMinScore(65); setOrphanOnly(false); }} testId="kpi-strong-fit"
               title="Score ≥ 65. Ready to pitch — has reg readiness + EU rights + TA fit confirmed. Click to filter." />
          <Kpi label="Fit (≥50)" value={kpis.fit} sub={kpis.companies?.fit ? `${kpis.companies.fit} companies` : null} tone="teal"
               active={minScore === 50 && !orphanOnly} onClick={() => { setMinScore(50); setOrphanOnly(false); }} testId="kpi-fit"
               title="Score 50–64. Strong candidate but missing 1 input (rights or readiness). Click to filter." />
          <Kpi label="Borderline (25–49)" value={kpis.borderline} sub={kpis.companies?.borderline ? `${kpis.companies.borderline} companies` : null} tone="amber"
               active={minScore === 25 && !orphanOnly} onClick={() => { setMinScore(25); setOrphanOnly(false); }} testId="kpi-borderline"
               title="Score 25–49. Worth screening — has TA match, needs reg/rights verification. Click to filter." />
          <Kpi label="Orphan / Rare" value={kpis.orphan ?? 0} sub={kpis.companies?.orphan ? `${kpis.companies.orphan} companies` : null} tone="indigo" testId="kpi-orphan"
               active={orphanOnly} onClick={() => { setOrphanOnly(o => !o); setMinScore(0); }}
               title="Orphan-tagged assets that are ALREADY in your shortlist (Fit + Borderline) — it's a subset, not a separate pool. Per the deck: orphan carve-outs sit inside I&I, Heme, and Neuro · hospital-specialist channel · EU rights available/negotiable · ready-to-file/FDA-approved/Ph3-pivotal · no Phase IV. +10 score bonus already baked in. Click to filter the table to these rows only." />
        </div>
      )}

      {/* Orphan overlap explainer — appears below KPIs */}
      {kpis && (
        <div className="mb-5 text-[11px] text-slate-500 italic px-1" data-testid="abacus-orphan-note">
          Orphan / Rare counts the orphan-tagged assets <em>inside</em> your shortlist (Fit + Borderline). It's a subset, not a separate pool — every orphan asset is already counted in one of the tier cards above. Clicking it filters the table to those rows only.
          {kpis.companies?.universe && kpis.companies?.in_scope && kpis.companies.universe > kpis.companies.in_scope && (
            <span className="block mt-1.5 not-italic text-slate-400">
              <b>TAM context:</b> our DB holds <b>{kpis.companies.universe.toLocaleString()}</b> biotechs with Ph3/commercial/approved assets, but only <b>{kpis.companies.in_scope}</b> have at least one asset scoring ≥ 25 today. The rest sit below threshold — typically missing EU rights or reg readiness data. A bulk Perplexity refresh usually surfaces 5–10% of those into the shortlist.
            </span>
          )}
        </div>
      )}

      {/* TA Bucket chips */}
      {kpis?.ta_buckets && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">TA:</span>
          <button
            onClick={() => setTaFilter('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${taFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            data-testid="abacus-ta-all"
          >
            All
          </button>
          {taOptions.map(ta => (
            <button
              key={ta}
              onClick={() => setTaFilter(ta)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${taFilter === ta ? 'bg-[#00709B] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              data-testid={`abacus-ta-${ta.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {ta} <span className="opacity-60">({kpis.ta_buckets[ta] || 0})</span>
            </button>
          ))}
        </div>
      )}

      {/* Controls — search + refresh only. Min-score filtering is driven by the KPI cards above. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          placeholder="Search asset / company / indication…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="abacus-search-input"
        />
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="abacus-hide-known-toggle">
          <input
            type="checkbox"
            checked={hideKnown}
            onChange={e => setHideKnown(e.target.checked)}
            className="rounded"
          />
          Hide companies Abacus has already engaged
        </label>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          <span className="ml-1.5 text-xs">Refresh</span>
        </Button>
        <div className="ml-auto text-xs text-slate-500">
          Showing <b>{filteredItems.length}</b> of {data?.items?.length || 0}
        </div>
      </div>

      {/* Last bulk enrichment banner — persistent, manual-refresh only */}
      {lastRun && (
        <div className="mb-4 flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-xs" data-testid="abacus-last-run-banner">
          <div className="flex items-center gap-2 text-slate-700">
            <Sparkles className="w-3.5 h-3.5 text-[#00709B]" />
            <span className="font-semibold">Last bulk enrichment:</span>
            <span className="text-slate-600">
              {relTime(lastRun.updated_at || lastRun.started_at) || '—'}
              <span className="text-slate-400"> · {lastRun.scope || '—'} scope · {lastRun.completed || 0}/{lastRun.total || 0} assets</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-slate-600">
            <button
              type="button"
              onClick={openApplied}
              className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] hover:bg-emerald-100 hover:border-emerald-300 transition cursor-pointer"
              data-testid="abacus-last-run-applied-badge"
              title="Click to see exactly which fields were auto-applied to which assets, with citations."
            >
              {lastRun.auto_applied_total || 0} fields auto-applied
            </button>
            <button
              type="button"
              onClick={openReview}
              className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] hover:bg-amber-100 hover:border-amber-300 transition cursor-pointer"
              data-testid="abacus-last-run-pending-badge"
              title="Medium-confidence proposals that didn't auto-apply. Click to review and accept/reject each one."
            >
              {lastRun.review_total || 0} pending review
            </button>
            {lastRun.error_count > 0 && (
              <Badge variant="outline" className="text-[10px] py-0 bg-rose-50 border-rose-200 text-rose-800">
                {lastRun.error_count} errors
              </Badge>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={load}
              disabled={loading}
              data-testid="abacus-last-run-refresh"
              title="Pull the latest scores and enriched fields into this view"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
              <span className="ml-1.5">Refresh shortlist</span>
            </Button>
          </div>
        </div>
      )}

      {/* Table — no horizontal scroll on a standard 1920px viewport */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-14 text-center">Score</TableHead>
                <TableHead className="min-w-[200px]">Asset / Company</TableHead>
                <TableHead className="min-w-[200px]">Stage · TA</TableHead>
                <TableHead className="w-40">
                  Reg Readiness
                  <span className="block text-[9px] font-normal text-slate-400 normal-case mt-0.5" title="Manual override OR auto-applied by Perplexity bulk enrichment. Scores recalc on Refresh.">
                    manual · auto-enriched
                  </span>
                </TableHead>
                <TableHead className="w-32">
                  EU Rights
                  <span className="block text-[9px] font-normal text-slate-400 normal-case mt-0.5" title="Manual override OR auto-applied by Perplexity bulk enrichment. Scores recalc on Refresh.">
                    manual · auto-enriched
                  </span>
                </TableHead>
                <TableHead className="w-32">
                  Fit Override
                  <span className="block text-[9px] font-normal text-slate-400 normal-case mt-0.5" title="Manual only. Overrides the auto-tier from the score engine.">
                    manual only
                  </span>
                </TableHead>
                <TableHead className="min-w-[140px]">Why (auto)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                    Scoring assets…
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500 text-sm">
                    No assets match the current filters. Try lowering the minimum score or clearing the TA filter.
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredItems.map((row) => (
                <TableRow key={row.asset_id} data-testid={`abacus-row-${row.asset_id}`}>
                  <TableCell className="text-center">
                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${fitBadgeClass(row.score)}`}>
                      {row.score}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-900">{row.asset_name}</div>
                    <div className="text-xs text-slate-600 flex items-center gap-1.5">
                      <a
                        href={`/biotech/${row.company_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#00709B] hover:underline inline-flex items-center gap-1"
                      >
                        {row.company_name} <ExternalLink className="w-3 h-3" />
                      </a>
                      {row.company_country && <span className="text-slate-400">· {row.company_country}</span>}
                    </div>
                    {(row.ta_bucket || row.orphan) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {row.ta_bucket && (
                          <Badge variant="outline" className="text-[10px] py-0">
                            {row.ta_bucket}{row.ta_sub_bucket ? ` › ${row.ta_sub_bucket}` : ''}
                          </Badge>
                        )}
                        {row.orphan && (
                          <Badge className="text-[10px] py-0 bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-indigo-100">
                            Orphan
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs font-medium text-slate-700">{row.stage || '—'}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{row.therapeutic_area || '—'}</div>
                    {row.indication && (
                      <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{row.indication}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.regulatory_readiness}
                      onValueChange={(v) => patchAsset(row.asset_id, { regulatory_readiness: v })}
                      disabled={savingId === row.asset_id}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`abacus-reg-${row.asset_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REG_READINESS_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.eu_rights_status}
                      onValueChange={(v) => patchAsset(row.asset_id, { eu_rights_status: v })}
                      disabled={savingId === row.asset_id}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`abacus-rights-${row.asset_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EU_RIGHTS_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.abacus_fit_status}
                      onValueChange={(v) => patchAsset(row.asset_id, { abacus_fit_status: v })}
                      disabled={savingId === row.asset_id}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`abacus-fit-${row.asset_id}`}>
                        {row.abacus_fit_status === 'not_screened' ? (
                          <span className="text-slate-500 italic">{autoTierLabel(row.score)}</span>
                        ) : (
                          <SelectValue />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {FIT_STATUS_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="text-[11px] text-slate-600 line-clamp-2 cursor-help max-w-xs">
                            {(row.score_reasons || []).join(' · ') || '—'}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-md text-xs">
                          {(row.score_reasons || []).map((r, i) => (
                            <div key={i}>{r}</div>
                          ))}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-4">
        Scoring: +30 EU-filed · +25 FDA-approved (EU bridge) · +15 Ph3-pivotal starting · +20 EU rights available · +10 negotiable · +10 TA fit · +10 Cardio uplift · +10 Orphan / rare-disease · +5 net-new to Abacus.
        Hard excludes: Phase 1/2, preclinical, EU rights taken, manually marked out of scope.
      </p>

      {/* Bulk Enrichment confirmation + progress modal */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o && bulkJob?.status !== 'running') { setBulkOpen(false); setBulkJob(null); } }}>
        <DialogContent className="max-w-lg" data-testid="abacus-bulk-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#00709B]" />
              Bulk Web Enrichment
            </DialogTitle>
            <DialogDescription className="text-xs">
              Runs Perplexity Sonar Pro across the selected asset set to fetch live web evidence and propose field updates. Choose your scope and auto-apply mode below.
            </DialogDescription>
          </DialogHeader>

          {!bulkJob && (
            <div className="space-y-4 py-3 text-sm">
              {/* Scope picker */}
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Scope — which assets to enrich</label>
                <Select value={bulkScope} onValueChange={setBulkScope}>
                  <SelectTrigger className="h-9 text-sm" data-testid="bulk-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="borderline">Borderline only (score 25–49)</SelectItem>
                    <SelectItem value="fit_and_borderline">Fit + Borderline (score ≥ 25)</SelectItem>
                    <SelectItem value="above_score">Above current minimum score ({minScore}+)</SelectItem>
                    <SelectItem value="all_visible">All in-scope (not hard-excluded)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">
                  {bulkScope === 'borderline' && 'Targets the assets that are 1–2 fields away from promotion to Fit.'}
                  {bulkScope === 'fit_and_borderline' && 'Refresh existing Fit assets too — useful when their data is stale or to spot Fit→Strong Fit promotions.'}
                  {bulkScope === 'above_score' && `Uses the minimum-score filter you set in the toolbar (currently ${minScore}+).`}
                  {bulkScope === 'all_visible' && 'Full universe except hard-excluded (Phase 1/2, EU rights taken, manually out-of-scope). Largest scope — slowest + most expensive.'}
                </div>
              </div>

              {/* Auto-apply mode picker */}
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Auto-apply mode</label>
                <Select value={bulkAutoApply} onValueChange={setBulkAutoApply}>
                  <SelectTrigger className="h-9 text-sm" data-testid="bulk-auto-apply-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conservative">Conservative — apply <b>high</b>-confidence findings only</SelectItem>
                    <SelectItem value="balanced">Balanced — apply <b>high + medium</b> confidence</SelectItem>
                    <SelectItem value="none">Review-only — queue everything, apply nothing</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Anything not auto-applied lands in the review queue for manual approval.
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
                <div><b>Estimated cost:</b> ~$0.03 per asset in Perplexity credits</div>
                <div><b>Estimated duration:</b> ~5 seconds per asset (5 parallel calls)</div>
                <div><b>Refresh behaviour:</b> the shortlist table is <i>not</i> auto-refreshed when the job finishes — click "Refresh shortlist" in the banner when you're ready.</div>
              </div>
            </div>
          )}

          {bulkJob && (
            <div className="space-y-3 py-3">
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="font-semibold">
                    {bulkJob.status === 'completed' ? '✓ Completed' : 'Researching the web…'}
                  </span>
                  <span className="text-slate-500">{bulkJob.completed} / {bulkJob.total}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-[#00709B] transition-all"
                    style={{ width: `${bulkJob.total ? (bulkJob.completed / bulkJob.total * 100) : 0}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <div className="font-bold text-emerald-900 text-lg">{bulkJob.auto_applied_total ?? 0}</div>
                  <div className="text-emerald-700">Fields auto-applied</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="font-bold text-amber-900 text-lg">{bulkJob.review_total ?? 0}</div>
                  <div className="text-amber-700">Pending review</div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded p-2">
                  <div className="font-bold text-rose-900 text-lg">{bulkJob.error_count ?? 0}</div>
                  <div className="text-rose-700">Errors</div>
                </div>
              </div>
              {bulkJob.status === 'completed' && (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                  Click <b>Refresh shortlist</b> in the banner above the table when you're ready to see promotions. Medium-confidence proposals are stored — a Review Queue page can be added next.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!bulkJob ? (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel">Cancel</Button>
                <Button
                  onClick={startBulkEnrich}
                  disabled={bulkStarting}
                  className="bg-[#00709B] hover:bg-[#005a7d] text-white"
                  data-testid="bulk-start"
                >
                  {bulkStarting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" />}
                  Start Bulk Enrichment
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => { setBulkOpen(false); }} data-testid="bulk-close">
                {bulkJob.status === 'completed' ? 'Close' : 'Run in background'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Web Enrichment review modal — fed by Perplexity Sonar Pro */}
      <Dialog open={enrichOpen} onOpenChange={setEnrichOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="abacus-enrich-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#00709B]" />
              Web Enrichment
              {enrichProposal?.asset_name && (
                <span className="text-sm font-normal text-slate-500">
                  · {enrichProposal.asset_name} · {enrichProposal.company_name}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Perplexity Sonar Pro researched this asset on the public web. Review each proposed change below and accept only what you trust. Nothing is auto-applied.
            </DialogDescription>
          </DialogHeader>

          {enrichLoading && (
            <div className="py-16 text-center text-sm text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
              Researching the public web (this takes 20–40 seconds)…
            </div>
          )}

          {!enrichLoading && enrichProposal && (
            <div className="space-y-3">
              {Object.entries(enrichProposal.proposed || {}).map(([field, block]) => {
                if (!block || typeof block !== 'object') return null;
                const pv = block.proposed_value;
                const cv = enrichProposal.current?.[field];
                const conf = block.confidence || 'unknown';
                const meaningful = pv && pv !== 'unknown' && pv !== 'null' && String(pv) !== String(cv || '');
                if (!meaningful) {
                  return (
                    <div key={field} className="border border-slate-100 rounded p-2 text-xs text-slate-400" data-testid={`enrich-field-${field}`}>
                      <div className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{field.replace(/_/g, ' ')}</div>
                      <div className="italic mt-1">No verifiable update from the web — keeping current value.</div>
                      {block.rationale && <div className="text-[10px] text-slate-400 mt-0.5">↳ {block.rationale}</div>}
                    </div>
                  );
                }
                const confColor = conf === 'high' ? 'bg-emerald-100 text-emerald-800' : conf === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600';
                const isStage = field === 'stage';
                return (
                  <div key={field}
                       className={`border rounded p-3 ${isStage ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}
                       data-testid={`enrich-field-${field}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!enrichAccepted[field]}
                        onChange={(e) => setEnrichAccepted(prev => ({ ...prev, [field]: e.target.checked }))}
                        className="mt-1 rounded"
                        data-testid={`enrich-accept-${field}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-slate-900 uppercase tracking-wider text-[11px]">{field.replace(/_/g, ' ')}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${confColor}`}>confidence: {conf}</span>
                          {isStage && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold">⚠ Stage change — review carefully</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Current (CRM)</div>
                            <div className="text-xs text-slate-700 mt-0.5">{cv || <span className="italic text-slate-400">(empty)</span>}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wider">Proposed (web)</div>
                            <div className="text-xs text-slate-900 mt-0.5 font-medium">{String(pv)}</div>
                          </div>
                        </div>
                        {block.rationale && (
                          <div className="text-[11px] text-slate-500 mt-2 italic">↳ {block.rationale}</div>
                        )}
                      </div>
                    </label>
                  </div>
                );
              })}

              {/* Citations */}
              {enrichProposal.citations?.length > 0 && (
                <div className="border-t pt-3 mt-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Sources</div>
                  <ol className="space-y-0.5 text-xs">
                    {enrichProposal.citations.map((c, i) => (
                      <li key={i} className="text-slate-600">
                        <span className="text-slate-400 mr-1">[{i + 1}]</span>
                        <a href={c} target="_blank" rel="noreferrer" className="text-[#00709B] hover:underline break-all">{c}</a>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setEnrichOpen(false)} disabled={enrichApplying} data-testid="enrich-cancel">
              Cancel
            </Button>
            <Button
              onClick={applyEnrich}
              disabled={enrichLoading || enrichApplying || Object.values(enrichAccepted).filter(Boolean).length === 0}
              className="bg-[#00709B] hover:bg-[#005a7d] text-white"
              data-testid="enrich-apply"
            >
              {enrichApplying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply {Object.values(enrichAccepted).filter(Boolean).length} selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REVIEW QUEUE — opened by clicking the "pending review" badge */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="abacus-review-queue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-amber-700" />
              Pending review — medium-confidence findings
            </DialogTitle>
            <DialogDescription className="text-xs">
              Perplexity proposed these values but flagged them as medium confidence. Accept individually to apply, or reject to dismiss. Anything you ignore stays in the queue.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {reviewLoading && (
              <div className="text-center py-12 text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading queue…</div>
            )}
            {!reviewLoading && reviewItems.length === 0 && (
              <div className="text-center py-12 text-slate-500 text-sm">Nothing left to review — queue is clear.</div>
            )}
            {!reviewLoading && reviewItems.map((item) => {
              const key = `${item.asset_id}__${item.field}`;
              const busy = reviewActioningId === key;
              return (
                <div key={key} className="border border-slate-200 rounded-lg p-4 mb-3 bg-white" data-testid={`review-item-${key}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{(item.field || '').replace(/_/g, ' ')}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">confidence: {item.confidence || 'medium'}</span>
                    <span className="ml-auto text-[10px] text-slate-400">{relTime(item.created_at)}</span>
                  </div>
                  <div className="mb-2">
                    <div className="text-sm font-semibold text-slate-900">{item.asset_name}</div>
                  </div>
                  <div className="mb-2 p-2 rounded bg-slate-50 border border-slate-100">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Proposed value</div>
                    <div className="text-sm text-slate-800">{String(item.proposed_value ?? '—').slice(0, 600)}</div>
                  </div>
                  {item.rationale && (
                    <div className="text-xs text-slate-600 italic mb-2">↳ {item.rationale}</div>
                  )}
                  {item.citations && item.citations.length > 0 && (
                    <div className="text-[10px] text-slate-500 mb-3">
                      Sources: {item.citations.slice(0, 4).map((c, i) => (
                        <a key={i} href={c} target="_blank" rel="noreferrer" className="text-[#00709B] hover:underline mr-2">[{i + 1}]</a>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => actionReviewItem(item, 'accept')}
                      disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                      data-testid={`review-accept-${key}`}
                    >
                      {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      Accept · apply to asset
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => actionReviewItem(item, 'reject')}
                      disabled={busy}
                      className="text-xs h-8"
                      data-testid={`review-reject-${key}`}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <div className="text-xs text-slate-500 mr-auto">
              {reviewItems.length} {reviewItems.length === 1 ? 'finding' : 'findings'} remaining · click "Refresh shortlist" after you're done to update scores.
            </div>
            <Button variant="outline" size="sm" onClick={() => setReviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUTO-APPLIED AUDIT — opened by clicking the "fields auto-applied" badge */}
      <Dialog open={appliedOpen} onOpenChange={setAppliedOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="abacus-applied-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-700" />
              Auto-applied changes — last bulk run
            </DialogTitle>
            <DialogDescription className="text-xs">
              Read-only audit of every field that Perplexity auto-applied to your shortlist during the most recent bulk enrichment. Each row shows the new value, why Perplexity proposed it, and the source citations.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {appliedLoading && (
              <div className="text-center py-12 text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
            )}
            {!appliedLoading && appliedItems.length === 0 && (
              <div className="text-center py-12 text-slate-500 text-sm">No auto-applied changes recorded for the last run.</div>
            )}
            {!appliedLoading && appliedItems.map((item, i) => (
              <div key={`${item.asset_id}__${item.field}__${i}`} className="border border-slate-200 rounded-lg p-4 mb-3 bg-white" data-testid={`applied-item-${i}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{(item.field || '').replace(/_/g, ' ')}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">confidence: {item.confidence || 'high'}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">auto-applied</span>
                  <span className="ml-auto text-[10px] text-slate-400">{relTime(item.applied_at)}</span>
                </div>
                <div className="mb-2 text-sm font-semibold text-slate-900">{item.asset_name}</div>
                <div className="p-2 rounded bg-emerald-50 border border-emerald-100 mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Applied value</div>
                  <div className="text-sm text-slate-800">{String(item.applied_value ?? '—').slice(0, 600)}</div>
                </div>
                {item.rationale && <div className="text-xs text-slate-600 italic mb-2">↳ {item.rationale}</div>}
                {item.citations && item.citations.length > 0 && (
                  <div className="text-[10px] text-slate-500">
                    Sources: {item.citations.slice(0, 4).map((c, j) => (
                      <a key={j} href={c} target="_blank" rel="noreferrer" className="text-[#00709B] hover:underline mr-2">[{j + 1}]</a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <div className="text-xs text-slate-500 mr-auto">{appliedItems.length} changes from last run</div>
            <Button variant="outline" size="sm" onClick={() => setAppliedOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScoreRow({ plus, label, desc }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="inline-block min-w-[34px] font-bold text-emerald-700 text-xs tabular-nums">{plus}</span>
      <div className="flex-1">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="text-slate-500"> — {desc}</span>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone, active = false, onClick, testId, title }) {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    teal: 'bg-teal-50 border-teal-200 text-teal-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    violet: 'bg-violet-50 border-violet-200 text-violet-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  };
  const activeRingMap = {
    slate: 'ring-2 ring-slate-500',
    emerald: 'ring-2 ring-emerald-500 shadow-sm',
    teal: 'ring-2 ring-teal-500 shadow-sm',
    amber: 'ring-2 ring-amber-500 shadow-sm',
    rose: 'ring-2 ring-rose-500',
    violet: 'ring-2 ring-violet-500',
    indigo: 'ring-2 ring-indigo-500',
  };
  const clickable = typeof onClick === 'function';
  const cls = `rounded-lg border px-4 py-3 transition-all ${toneMap[tone] || toneMap.slate} ${
    clickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''
  } ${active ? activeRingMap[tone] || activeRingMap.slate : ''}`;
  return (
    <div className={cls} onClick={onClick} data-testid={testId} title={title}>
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {value?.toLocaleString?.() ?? value}
      </div>
      {sub && (
        <div className="text-[10px] font-medium opacity-60 mt-0.5">{sub}</div>
      )}
    </div>
  );
}
